/**
 * @file ChecklistTable.tsx
 * @description 合約評審檢查項目表格（受控組件）。
 *
 * 檢查項目按 scheme 分版（TOP 12 項 / TAP 14 項，ECO 回落 TOP），
 * 本組件不硬編題數：完全按 API 返回的 checklist（存量 label 快照）渲染。
 *
 * value: N 個 ContractReviewChecklistItem（題數由後端預填決定）
 * onChange: 整個陣列回傳（不可變更新），父層管狀態。
 * readOnly: 已提交 / 已決議的單據展示模式。
 */

import { Input, Radio, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CONTRACT_REVIEW_CHECKLIST_LABELS, type ContractReviewChecklistItem } from '@zhcb/shared';

interface Props {
  /** 當前檢查項目的值（TOP 12 項 / TAP 14 項） */
  value: ContractReviewChecklistItem[];
  /** 變更回呼；不傳即只讀 */
  onChange?: (next: ContractReviewChecklistItem[]) => void;
  /** 顯式禁用編輯（覆蓋 onChange 是否存在的判斷） */
  readOnly?: boolean;
}

/**
 * 備注必填判斷（與後端 lead-submit refine 一致）：
 *   - 題 7（序108 新語義：「是」＝沒有特殊要求；「否」＝有特殊要求）選「否」時必填說明；
 *   - 末位「其它」項（TOP 版第 12 題、TAP 版第 14 題）選「是」時必填。
 * 此處用於 UI 提示（status='error'），最終校驗由後端把關。
 */
function noteRequired(no: number, answer: 'yes' | 'no', lastNo: number): boolean {
  if (no === 7) return answer === 'no';
  if (no === lastNo) return answer === 'yes';
  return false;
}

export function ChecklistTable({ value, onChange, readOnly }: Props) {
  const disabled = readOnly || !onChange;
  // 末位項即「其它」兜底項（題號 = 清單長度）
  const lastNo = value.length > 0 ? value[value.length - 1]!.no : 0;

  const update = (no: number, patch: Partial<ContractReviewChecklistItem>) => {
    if (!onChange) return;
    onChange(value.map((it) => (it.no === no ? { ...it, ...patch } : it)));
  };

  const columns: ColumnsType<ContractReviewChecklistItem> = [
    { title: '序號', dataIndex: 'no', width: 60 },
    {
      title: '檢查項目',
      dataIndex: 'label',
      render: (_, row) => row.label || CONTRACT_REVIEW_CHECKLIST_LABELS[row.no - 1],
    },
    {
      title: '是 / 否',
      width: 210,
      render: (_, row) => (
        <Radio.Group
          value={row.answer}
          disabled={disabled}
          onChange={(e) => update(row.no, { answer: e.target.value })}
        >
          {/* 序108：題 7 選項語義明示——是＝沒有特殊要求；否＝有特殊要求（須說明） */}
          {row.no === 7 ? (
            <>
              <Radio value="yes">是，沒有特殊要求</Radio>
              <Radio value="no">否，有特殊要求</Radio>
            </>
          ) : (
            <>
              <Radio value="yes">是</Radio>
              <Radio value="no">否</Radio>
            </>
          )}
        </Radio.Group>
      ),
    },
    {
      title: '備註',
      width: 280,
      render: (_, row) => (
        <Input
          value={row.note ?? ''}
          disabled={disabled}
          placeholder={noteRequired(row.no, row.answer, lastNo) ? '必填' : '可選'}
          status={
            noteRequired(row.no, row.answer, lastNo) && !row.note?.trim() ? 'error' : undefined
          }
          onChange={(e) => update(row.no, { note: e.target.value })}
        />
      ),
    },
  ];

  return (
    <Table
      rowKey="no"
      size="small"
      pagination={false}
      columns={columns}
      dataSource={value}
      title={() => `二、合約評審（${value.length} 項）`}
    />
  );
}
